import axios from 'axios';
import { useEffect, useState } from 'react';
import {
    Badge, Button, DataTable, Empty, FormDrawer, FormSection, IconButton, InfoBar,
    Input, Modal, PageHeader, Select, Spin, Switch, TableStatusBar, useAlert,
} from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { formatTaille, ouvrirBase64, telechargerBase64 } from '../../Functions/documents';
import {
    ChevronLeft, ChevronRight, Download, Eye, FileText, Filter, FolderOpen,
    Loader2, Pencil, Plus, Save, Search, Trash2,
} from 'lucide-react';

/** POST générique chiffré vers un endpoint Documents — retourne res.Value. */
function postApi(authHeader: () => string, url: string, payload: any): Promise<any> {
    return axios.request({
        method: 'post',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + url,
        headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
        data: Crypter.encrypt(JSON.stringify(payload))
    }).then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value);
}

/** GET avec ?CipherId= / ?CipherFiltre= — retourne res.Value. */
function getCipher(authHeader: () => string, url: string, cipher: string): Promise<any> {
    return axios.request({
        method: 'get',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + url + cipher,
        headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
    }).then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value);
}

function pick(row: any, ...keys: string[]) {
    if (!row) return undefined;
    for (const k of keys) {
        if (row[k] != null && row[k] !== '') return row[k];
    }
    return undefined;
}

function formatDate(value: any): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function today() {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function joursAvant(n: number) {
    const d = new Date(); d.setDate(d.getDate() - n)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const CANAL_OPTIONS = [
    { value: '', label: 'Tous les canaux' },
    { value: 'ONLINE', label: 'En ligne' },
    { value: 'RDV', label: 'Rendez-vous' },
];

export default function GestionDocuments() {

    // USER TOKEN
    const authHeader = useAuthHeader()
    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))

    // Droits (liste reçue à la connexion)
    const habilitations: any[] = (() => {
        try { return JSON.parse(Crypter.decrypt(localStorage.getItem(datas.Habilitations) || '')) || [] } catch { return [] }
    })()
    const isAdmin = ['SUADMIN', 'ADMIN', 'IT'].includes(user.RoleId)
    const canTypes = isAdmin || habilitations.some((h: any) => h.CodeHabi === 'D13')

    const [tab, setTab] = useState<'pieces' | 'types'>('pieces')
    const [types, setTypes] = useState<any[]>([])

    useEffect(() => {
        // Types actifs → filtres + libellés (GET /api/Documents/TypesDocuments)
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Documents.TypesDocuments,
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        })
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) setTypes(res.Result || [])
            })
            .catch(() => { })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const TABS = [
        { key: 'pieces' as const, label: 'Pièces déposées en ligne', icon: <FolderOpen size={15} /> },
        { key: 'types' as const, label: 'Types de pièces', icon: <FileText size={15} /> },
    ];

    return (
        <div>
            <PageHeader
                icon={<FileText />}
                title="Documents"
                subtitle="GED — pièces jointes déposées en ligne et types de pièces"
            />

            <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-xl bg-[var(--eb-bg)] border border-[var(--eb-border)] mb-4">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        type='button'
                        onClick={() => setTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                            ${tab === t.key
                                ? 'bg-[var(--eb-primary)] text-white shadow-sm'
                                : 'text-[var(--eb-text-muted)] hover:text-[var(--eb-text)]'}`}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'pieces'
                ? <SectionPiecesEnLigne authHeader={authHeader} types={types} isAdmin={isAdmin} user={user} />
                : <SectionTypesPieces authHeader={authHeader} canEdit={canTypes} />}
        </div>
    );
}

/* ================================================================== */
/*  Onglet 1 — Pièces déposées en ligne  (GET api/Documents/EnLigne)  */
/* ================================================================== */
function SectionPiecesEnLigne(props: { authHeader: () => string; types: any[]; isAdmin: boolean; user: User }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [docs, setDocs] = useState<any[]>([])
    const [pagination, setPagination] = useState<any>({ Total: 0, Page: 1, TaillePage: 50, NbPages: 1 })

    // Filtres (envoyés dans CipherFiltre)
    const [recherche, setRecherche] = useState('')
    const [typeDoc, setTypeDoc] = useState('')
    const [canal, setCanal] = useState('')
    const [dateDebut, setDateDebut] = useState(joursAvant(7))
    const [dateFin, setDateFin] = useState(today())
    const [page, setPage] = useState(1)

    // Visualisation en cours (chargement du contenu)
    const [viewingId, setViewingId] = useState<number | null>(null)

    const [selected, setSelected] = useState<any>()

    useEffect(() => {
        LoadData(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData(p = page) {
        setLoading(true)
        const filtre: any = { Page: p, TaillePage: 50 }
        if (recherche.trim()) filtre.Recherche = recherche.trim()
        if (typeDoc) filtre.TypeDocument = typeDoc
        if (canal) filtre.Canal = canal
        if (dateDebut) filtre.DateDebut = dateDebut
        if (dateFin) filtre.DateFin = dateFin
        if (props.isAdmin && props.user.CodeFiliale) { /* périmètre géré côté serveur */ }

        getCipher(props.authHeader, datas.API_LINKs.Documents.EnLigne + '?CipherFiltre=',
            Crypter.encrypt(JSON.stringify(filtre)))
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    const result = res.Result || {}
                    setDocs((result.Documents || []).map((d: any, i: number) => ({ id: d.Id ?? i, ...d })))
                    setPagination(result.Pagination || { Total: 0, Page: 1, TaillePage: 50, NbPages: 1 })
                    setPage(result.Pagination?.Page || p)
                }
                else {
                    setDocs([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setDocs([])
                alert.error('Error', error)
                console.log(error)
            })
            .finally(() => setLoading(false));
    }

    function applyFilters() {
        setPage(1)
        LoadData(1)
    }

    /** Voir / télécharger : GET Download?CipherId=<Id chiffré> → ContenuBase64. */
    function openDoc(row: any, download = false) {
        setViewingId(row.Id)
        getCipher(props.authHeader, datas.API_LINKs.Documents.Download, Crypter.encrypt(String(row.Id)))
            .then((res: any) => {
                if (res.StatutCode == 200 && res.Result?.ContenuBase64) {
                    if (download) telechargerBase64(res.Result.ContenuBase64, res.Result.NomFichier || row.NomFichier, res.Result.MimeType)
                    else ouvrirBase64(res.Result.ContenuBase64, res.Result.MimeType)
                }
                else alert.warning(res.Message || 'Contenu indisponible')
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setViewingId(null));
    }

    const columns: DataTableColumn[] = [
        {
            field: 'DateAjout', headerName: 'Déposé le', width: 130,
            renderCell: ({ row }) => <span className="text-xs">{formatDate(row.DateAjout)}</span>,
        },
        {
            field: 'Client', headerName: 'Client', width: 190,
            renderCell: ({ row }) => (
                <div className="leading-tight">
                    <div className="font-medium text-sm">{[row.PrenomClient, row.NomClient].filter(Boolean).join(' ') || '—'}</div>
                    <div className="text-xs text-[var(--eb-text-muted)]">{row.Telephone || ''}</div>
                </div>
            ),
        },
        {
            field: 'CodeDemande', headerName: 'Demande', width: 170,
            renderCell: ({ row }) => (
                <div className="leading-tight">
                    <div className="font-mono text-xs">{row.CodeDemande || '—'}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                        {row.Canal && <Badge color={row.Canal === 'RDV' ? 'violet' : 'blue'}>{row.Canal}</Badge>}
                        {row.NumeroTicket && <span className="text-xs text-[var(--eb-text-muted)]">n° {row.NumeroTicket}</span>}
                    </div>
                </div>
            ),
        },
        {
            field: 'LibelleOperation', headerName: 'Opération', width: 160,
            renderCell: ({ row }) => <span className="text-sm">{row.LibelleOperation || row.CodeOperation || '—'}</span>,
        },
        {
            field: 'TypeDocument', headerName: 'Type', width: 170,
            renderCell: ({ row }) => (
                <div className="leading-tight">
                    <Badge color="violet">{row.TypeDocument || '—'}</Badge>
                    <div className="text-xs text-[var(--eb-text-muted)] mt-0.5">{row.LibelleType || ''}</div>
                </div>
            ),
        },
        {
            field: 'NomFichier', headerName: 'Fichier', width: 180,
            renderCell: ({ row }) => (
                <div className="leading-tight">
                    <div className="text-sm truncate max-w-[170px]" title={row.NomFichier}>{row.NomFichier || '—'}</div>
                    <div className="text-xs text-[var(--eb-text-muted)]">{formatTaille(row.TailleOctets)}</div>
                </div>
            ),
        },
        {
            field: 'DeposePar', headerName: 'Déposé par', width: 90,
            renderCell: ({ row }) => (
                <Badge color={row.DeposePar === 'AGENT' ? 'amber' : 'green'}>{row.DeposePar || 'CLIENT'}</Badge>
            ),
        },
        {
            field: 'Actions', headerName: 'Actions', width: 90, sortable: false,
            renderCell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <IconButton
                        tooltip="Voir la pièce"
                        color="primary"
                        size={30}
                        disabled={viewingId === row.Id}
                        onClick={(e) => { e.stopPropagation(); openDoc(row) }}
                    >
                        {viewingId === row.Id ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
                    </IconButton>
                    <IconButton
                        tooltip="Télécharger"
                        color="default"
                        size={30}
                        onClick={(e) => { e.stopPropagation(); openDoc(row, true) }}
                    >
                        <Download size={15} />
                    </IconButton>
                </div>
            ),
        },
    ];

    return (
        <div>
            {/* Filtres */}
            <div className="eb-card mb-3">
                <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--eb-text-muted)]">
                    <Filter size={14} />
                    Filtres
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Input
                        label="Recherche"
                        value={recherche}
                        onChange={(e: any) => setRecherche(e.target.value)}
                        placeholder="Client, tél., demande, fichier…"
                        startIcon={<Search size={15} />}
                    />
                    <Select
                        label="Type de pièce"
                        value={typeDoc}
                        onChange={setTypeDoc}
                        options={[{ value: '', label: 'Tous les types' },
                            ...props.types.map((t: any) => ({ value: t.CodeTypeDoc, label: t.Libelle || t.CodeTypeDoc }))]}
                    />
                    <Select
                        label="Canal"
                        value={canal}
                        onChange={setCanal}
                        options={CANAL_OPTIONS}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <Input label="Du" type="date" value={dateDebut} onChange={(e: any) => setDateDebut(e.target.value)} />
                        <Input label="Au" type="date" value={dateFin} onChange={(e: any) => setDateFin(e.target.value)} />
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <Button size='sm' variant='secondary' startIcon={<Search size={15} />} onClick={applyFilters} loading={loading}>
                        Rechercher
                    </Button>
                </div>
            </div>

            <Spin spinning={loading}>
                <div className="eb-table-wrap">
                    {docs.length === 0 && !loading ? (
                        <div className="eb-card">
                            <Empty
                                icon={<FileText size={44} strokeWidth={1.2} />}
                                description="Aucune pièce déposée sur la période"
                            />
                        </div>
                    ) : (
                        <DataTable
                            onRowClick={(row: any) => setSelected(row)}
                            rows={docs}
                            height={420}
                            columns={columns}
                            searchable
                            emptyText="Aucune pièce déposée"
                        />
                    )}
                </div>
            </Spin>

            {/* Pagination serveur */}
            <div className="mt-2 flex items-center justify-between">
                <TableStatusBar count={docs.length} label={pagination.Total ? `${docs.length} affichée(s) / ${pagination.Total}` : undefined} loading={loading} onRefresh={() => LoadData(page)} />
                <div className="flex items-center gap-2 text-sm text-[var(--eb-text-muted)]">
                    <IconButton tooltip="Page précédente" size={28} disabled={page <= 1} onClick={() => LoadData(page - 1)}>
                        <ChevronLeft size={15} />
                    </IconButton>
                    <span>Page {pagination.Page || page} / {pagination.NbPages || 1}</span>
                    <IconButton tooltip="Page suivante" size={28} disabled={page >= (pagination.NbPages || 1)} onClick={() => LoadData(page + 1)}>
                        <ChevronRight size={15} />
                    </IconButton>
                </div>
            </div>

            <InfoBar data={[
                { label: 'Client', value: selected && [selected.PrenomClient, selected.NomClient].filter(Boolean).join(' ') },
                { label: 'Demande', value: pick(selected, 'CodeDemande') },
                { label: 'Type', value: pick(selected, 'LibelleType', 'TypeDocument') },
                { label: 'Fichier', value: pick(selected, 'NomFichier') },
                { label: 'Taille', value: selected && formatTaille(selected.TailleOctets) },
                { label: 'Agence', value: pick(selected, 'LibelleFiliale', 'CodeFiliale') },
            ]} />
        </div>
    );
}

/* ================================================================== */
/*  Onglet 2 — Types de pièces  (POST api/Documents/ManageTypes)      */
/* ================================================================== */
function SectionTypesPieces(props: { authHeader: () => string; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [types, setTypes] = useState<any[]>([])

    // Drawer création / renommage
    const [drawer, setDrawer] = useState<null | { mode: 'ADD' | 'UPDATE'; record?: any }>(null)
    const [fCode, setFCode] = useState('')
    const [fLibelle, setFLibelle] = useState('')

    // Confirmation suppression
    const [confirm, setConfirm] = useState<any | null>(null)

    const [selected, setSelected] = useState<any>()

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        setLoading(true)
        postApi(props.authHeader, datas.API_LINKs.Documents.ManageTypes, { Option: 'LIST' })
            .then((res: any) => {
                if (res.StatutCode == 200) setTypes((res.Result || []).map((t: any, i: number) => ({ id: t.CodeTypeDoc ?? i, ...t })))
                else { setTypes([]); alert.warning(res.Message) }
            })
            .catch((error: any) => { setTypes([]); alert.error('Error', error); console.log(error) })
            .finally(() => setLoading(false));
    }

    function openAdd() {
        setFCode(''); setFLibelle('')
        setDrawer({ mode: 'ADD' })
    }

    function openRename(record: any) {
        setFCode(record.CodeTypeDoc); setFLibelle(record.Libelle || '')
        setDrawer({ mode: 'UPDATE', record })
    }

    function handleSave() {
        if (!fCode.trim() || !fLibelle.trim()) {
            alert.warning('Code et libellé requis.')
            return
        }
        if (!/^[A-Z0-9_]+$/.test(fCode.trim())) {
            alert.warning('Code invalide : lettres majuscules, chiffres et _ uniquement (ex. JUSTIF_DOMICILE).')
            return
        }
        setSaving(true)
        const payload: any = drawer?.mode === 'ADD'
            ? { Option: 'ADD', CodeTypeDoc: fCode.trim(), Libelle: fLibelle.trim() }
            : { Option: 'UPDATE', CodeTypeDoc: fCode.trim(), Libelle: fLibelle.trim() }
        postApi(props.authHeader, datas.API_LINKs.Documents.ManageTypes, payload)
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setDrawer(null); LoadData() }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    function toggleActif(record: any) {
        setSaving(true)
        postApi(props.authHeader, datas.API_LINKs.Documents.ManageTypes,
            { Option: record.IsActif ? 'DESACTIVER' : 'ACTIVER', CodeTypeDoc: record.CodeTypeDoc })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); LoadData() }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    function handleDelete() {
        const record = confirm
        if (!record) return
        setSaving(true)
        postApi(props.authHeader, datas.API_LINKs.Documents.ManageTypes,
            { Option: 'DELETE', CodeTypeDoc: record.CodeTypeDoc })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setConfirm(null); LoadData() }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    const columns: DataTableColumn[] = [
        {
            field: 'CodeTypeDoc', headerName: 'Code', width: 190,
            renderCell: ({ value }) => <span className="font-mono text-xs">{value || '—'}</span>,
        },
        { field: 'Libelle', headerName: 'Libellé', width: 260 },
        {
            field: 'NbDocuments', headerName: 'Pièces liées', width: 100,
            renderCell: ({ value }) => <span>{value ?? 0}</span>,
        },
        {
            field: 'IsActif', headerName: 'Statut', width: 120,
            renderCell: ({ row }) => (
                <Badge color={row.IsActif !== false ? 'green' : 'gray'}>
                    {row.IsActif !== false ? 'Actif' : 'Désactivé'}
                </Badge>
            ),
        },
        ...(props.canEdit ? [{
            field: 'Actions', headerName: 'Actions', width: 190, sortable: false,
            renderCell: ({ row }: any) => (
                <div className="flex items-center gap-1.5">
                    <IconButton tooltip="Renommer" color="primary" size={30} onClick={(e: any) => { e.stopPropagation(); openRename(row) }}>
                        <Pencil size={15} />
                    </IconButton>
                    <Switch
                        checked={row.IsActif !== false}
                        onChange={() => toggleActif(row)}
                    />
                    <IconButton
                        tooltip={row.Supprimable ? 'Supprimer' : 'Non supprimable — des pièces de ce type existent'}
                        color="danger"
                        size={30}
                        disabled={!row.Supprimable}
                        onClick={(e: any) => { e.stopPropagation(); setConfirm(row) }}
                    >
                        <Trash2 size={15} />
                    </IconButton>
                </div>
            ),
        } as DataTableColumn] : []),
    ];

    return (
        <div>
            {!props.canEdit && (
                <div className="mb-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-700 dark:text-amber-300">
                    Lecture seule — le droit D13 « Gestion Documents (GED) » est requis pour gérer les types.
                </div>
            )}

            <Spin spinning={loading}>
                <div className="flex justify-end mb-3">
                    {props.canEdit && (
                        <Button size='sm' variant='primary' startIcon={<Plus size={16} />} onClick={openAdd}>
                            Nouveau type
                        </Button>
                    )}
                </div>

                <div className="eb-table-wrap">
                    {types.length === 0 && !loading ? (
                        <div className="eb-card">
                            <Empty icon={<FileText size={44} strokeWidth={1.2} />} description="Aucun type de pièce configuré" />
                        </div>
                    ) : (
                        <DataTable
                            onRowClick={(row: any) => setSelected(row)}
                            rows={types}
                            height={420}
                            columns={columns}
                            searchable
                            emptyText="Aucun type de pièce"
                        />
                    )}
                </div>
            </Spin>
            <TableStatusBar count={types.length} loading={loading} onRefresh={LoadData} />
            <InfoBar data={[
                { label: 'Code', value: pick(selected, 'CodeTypeDoc') },
                { label: 'Libellé', value: pick(selected, 'Libelle') },
                { label: 'Pièces liées', value: selected?.NbDocuments },
            ]} />

            {/* Drawer création / renommage */}
            <FormDrawer
                open={!!drawer}
                onClose={() => setDrawer(null)}
                title={drawer?.mode === 'ADD' ? 'Nouveau type de pièce' : 'Renommer le type'}
                hint="Le code est la clé technique : lettres majuscules, chiffres et _ uniquement."
                footer={
                    <>
                        <Button variant="outline" onClick={() => setDrawer(null)}>Annuler</Button>
                        <Button variant="primary" onClick={handleSave} loading={saving} startIcon={<Save size={15} />}>
                            Enregistrer
                        </Button>
                    </>
                }
            >
                <FormSection title="Type de pièce">
                    <Input
                        label="Code *"
                        value={fCode}
                        disabled={drawer?.mode === 'UPDATE'}
                        helperText={drawer?.mode === 'UPDATE' ? 'Clé technique — non modifiable' : 'Ex. JUSTIF_DOMICILE, CNI_RECTO'}
                        onChange={(e: any) => setFCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                    />
                    <Input
                        label="Libellé *"
                        value={fLibelle}
                        placeholder="Ex. Justificatif de domicile"
                        onChange={(e: any) => setFLibelle(e.target.value)}
                    />
                </FormSection>
            </FormDrawer>

            {/* Confirmation suppression */}
            <Modal
                open={!!confirm}
                onClose={() => setConfirm(null)}
                title="Supprimer le type de pièce"
                footer={
                    <>
                        <Button variant='outline' onClick={() => setConfirm(null)}>Annuler</Button>
                        <Button variant='danger' loading={saving} onClick={handleDelete}>Supprimer</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Supprimer le type <strong className="font-mono">{confirm?.CodeTypeDoc}</strong> ({confirm?.Libelle}) ?
                    Les pièces existantes de ce type sont conservées.
                </p>
            </Modal>
        </div>
    );
}
